/**
 * "Is this promo code / discount live right now?" — the dashboard's answer to the same question
 * the backend answers at checkout.
 *
 * MIRRORS tkt-backend/src/utils/scheduleWindow.ts and src/utils/absoluteDateTime.ts. Both PromoCode
 * and Discount carry the identical window shape (start:'now'|'scheduledTime' + startDate/startTime,
 * end:'salesEnd'|'scheduledTime' + endDate/endTime), and the badge an operator reads in the table
 * has to agree with what /validate tells a buyer. The only way to guarantee that is to compute it
 * the same way, so this is a deliberate mirror rather than a fresh implementation.
 *
 * WHAT IT REPLACES. The inventory table derived expiry with `new Date(`${p.endDate}T${p.endTime}`)`.
 * Now that endDate is a full ISO instant, that concatenation produces
 * "2026-09-01T00:00:00.000ZT23:59" → Invalid Date → every comparison false → **nothing ever reads
 * as expired**. An operator would see a dead code labelled Active indefinitely.
 *
 * DURATION ARITHMETIC, NOT LOCAL PARSING. `startDate` is already an absolute instant (the
 * organizer's local midnight of that day), and the time is a plain "HH:MM" offset added on top. So
 * the combination is `instant + hours + minutes` — pure duration maths, correct from any timezone
 * the browser happens to be in. Building a string and re-parsing it would reintroduce the local-
 * timezone ambiguity the absolute-instant convention exists to remove.
 */

/** "HH:MM" or "HH:MM:SS" → parts, or null. Mirrors the backend's parseTimeGuarded. */
function parseTimeGuarded(time?: string | null): { hour: number; minute: number } | null {
    if (!time) return null;
    const m = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(String(time).trim());
    if (!m) return null;
    return { hour: Number(m[1]), minute: Number(m[2]) };
}

/** An absolute-instant date plus an "HH:MM" offset. Mirrors combineAbsoluteDateTime. */
export function combineAbsoluteDateTime(date?: string | null, time?: string | null): Date | null {
    if (!date) return null;
    const base = new Date(date);
    if (Number.isNaN(base.getTime())) return null;
    const parsed = parseTimeGuarded(time);
    if (!parsed) return base;
    return new Date(base.getTime() + parsed.hour * 3_600_000 + parsed.minute * 60_000);
}

/**
 * WHICH CALENDAR DAY A STORED INSTANT NAMES. Mirrors the backend's `eventCalendarDay`.
 *
 * `EventPage.date` is the organizer's LOCAL MIDNIGHT of the event day, stored as a real instant —
 * so Kampala (+3) on 26 September is "2026-09-25T21:00:00.000Z". Reading the UTC day out of that,
 * whether by `.slice(0, 10)` or by handing the raw instant to `toLocaleDateString` in a browser west
 * of the organizer, gives the day BEFORE. Live on JamseshLive #2: the event is Saturday the 26th and
 * both the order timeline and the event card printed Friday the 25th.
 *
 * The ±12h pad maps the instant back into its own day for any timezone in (-12, +12), because every
 * such midnight lands within 12 hours of UTC midnight and the neighbouring days' midnights are a
 * full 24 hours away.
 */
const DAY_PAD_MS = 12 * 3_600_000;

export function eventCalendarDay(date?: string | null): string | null {
    if (!date) return null;
    const t = Date.parse(String(date));
    if (Number.isNaN(t)) return null;
    return new Date(t + DAY_PAD_MS).toISOString().slice(0, 10);
}

/**
 * THE INSTANT A CALENDAR DAY STARTS, where the event is. The inverse of `eventCalendarDay`.
 *
 * A date field hands back "2026-12-05". `new Date("2026-12-05")` is UTC midnight — 03:00 in
 * Kampala — and it passes the backend's absolute-instant guard, because it IS an instant; just the
 * wrong one. Every reader then adds `startTime` to it, so a 4 PM event read as 7 PM. The event
 * settings form saved exactly that; measured on moments 2026-09-30: 10 events, 5 of them upcoming
 * and on sale (AFROBEEZA advertised at 8 PM for a 5 PM start).
 *
 * The country's zone, not the browser's: an organiser editing from Nairobi or London is still
 * describing a day in Kampala. None of these zones observes DST, so midnight is one fixed offset.
 */
const COUNTRY_ZONE: Record<string, string> = {
    UG: "Africa/Kampala", KE: "Africa/Nairobi", TZ: "Africa/Dar_es_Salaam",
    RW: "Africa/Kigali", ZW: "Africa/Harare", ZA: "Africa/Johannesburg", NG: "Africa/Lagos",
};
const COUNTRY_NAMES: Record<string, string> = {
    uganda: "UG", kenya: "KE", tanzania: "TZ", rwanda: "RW", zimbabwe: "ZW", "south africa": "ZA", nigeria: "NG",
};

export function eventDayInstant(day: string, country?: string | null): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? "").trim());
    if (!m) return null;
    const raw = String(country ?? "").trim();
    const code = raw.length === 2 ? raw.toUpperCase() : COUNTRY_NAMES[raw.toLowerCase()];
    const timeZone = COUNTRY_ZONE[code ?? ""] ?? "Africa/Kampala";
    const utcMidnight = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    // The zone's offset at that moment, from its own wall clock: wall − utc.
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-US", {
            timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        }).formatToParts(new Date(utcMidnight)).map((p) => [p.type, p.value]),
    );
    const wall = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
    return new Date(utcMidnight - (wall - utcMidnight)).toISOString();
}

/**
 * Format an event's own day, in the organizer's reckoning rather than the viewer's.
 *
 * `timeZone: "UTC"` is load-bearing: `eventCalendarDay` has already resolved the correct day, and
 * formatting that day in any other zone would shift it straight back off by one.
 */
export function formatEventDay(
    date?: string | null,
    options: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric", year: "numeric" },
    locale = "en-US",
): string | null {
    const day = eventCalendarDay(date);
    if (!day) return null;
    return new Date(`${day}T00:00:00.000Z`).toLocaleDateString(locale, { ...options, timeZone: "UTC" });
}

/**
 * A STORED CLOCK TIME, READ BACK AS WRITTEN. "16:00" -> "4:00 PM".
 *
 * No timezone conversion, deliberately — and this is the whole point. A `startTime` is a bare
 * hour-of-day with no zone of its own: 16:00 means four in the afternoon WHERE THE EVENT IS.
 * Turning it into an instant and rendering that in the reader's zone answers a question nobody
 * asked, and on a printed ticket it would put a time on paper that nobody at the venue recognises.
 *
 * A time field cannot exceed 23:59, so it never rolls into another day.
 */
export function formatClock(time?: string | null, fallback = "-"): string {
    const m = String(time ?? "").trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) return fallback;
    const raw = Number(m[1]);
    if (!Number.isFinite(raw) || raw < 0 || raw > 23) return fallback;
    const suffix = raw >= 12 ? "PM" : "AM";
    const h = raw % 12 === 0 ? 12 : raw % 12;
    return `${h}:${m[2]} ${suffix}`;
}

/**
 * HAS THE EVENT'S DAY ACTUALLY BEEN AND GONE.
 *
 * NOT `new Date(date) < Date.now()`. That compares a DAY against an INSTANT, so an event becomes
 * "past" from its own local midnight onward — for the whole of the day it happens on. The order
 * timeline did exactly this and rendered a red "Event day — not checked in" at 09:22 on the morning
 * of a 16:00 show, seven hours before doors, on tickets that were about to be used.
 *
 * The day a midnight instant names runs from that instant to +24h, so it is over only once
 * `now` has passed `date + 24h` — the same rule as the backend's `eventDayCurrentBound`.
 *
 * This asks about the DAY. An event with a real start time that you want to compare against the
 * minute should combine date+time with `combineAbsoluteDateTime` instead.
 */
export function eventDayIsPast(date?: string | null, now: number = Date.now()): boolean {
    if (!date) return false;
    const t = Date.parse(String(date));
    if (Number.isNaN(t)) return false;
    return t <= now - 86_400_000;
}

export interface ScheduleWindow {
    start?: string | null;
    startDate?: string | null;
    startTime?: string | null;
    end?: string | null;
    endDate?: string | null;
    endTime?: string | null;
}

/**
 * `end: 'salesEnd'` carries no date of its own — it means "until the ticket type's own sales window
 * closes". Pass that as `salesEndFallback` when you have it; omit it and 'salesEnd' reads as
 * unbounded, which is what the backend does with no sales-end context.
 */
export function isScheduleActive(w: ScheduleWindow, now: Date = new Date(), salesEndFallback?: Date | null): boolean {
    if (w.start === "scheduledTime") {
        const startsAt = combineAbsoluteDateTime(w.startDate, w.startTime);
        if (startsAt && now < startsAt) return false;
    }
    if (w.end === "scheduledTime") {
        const endsAt = combineAbsoluteDateTime(w.endDate, w.endTime);
        if (endsAt && now > endsAt) return false;
    } else if (w.end === "salesEnd" && salesEndFallback) {
        if (now > salesEndFallback) return false;
    }
    return true;
}

/** Has this window not opened yet? Distinguishes "Scheduled" from "Active" in the table. */
export function isScheduledFuture(w: ScheduleWindow, now: Date = new Date()): boolean {
    if (w.start !== "scheduledTime") return false;
    const startsAt = combineAbsoluteDateTime(w.startDate, w.startTime);
    return !!startsAt && now < startsAt;
}

/** Has this window closed? */
export function isExpired(w: ScheduleWindow, now: Date = new Date(), salesEndFallback?: Date | null): boolean {
    if (w.end === "scheduledTime") {
        const endsAt = combineAbsoluteDateTime(w.endDate, w.endTime);
        return !!endsAt && now > endsAt;
    }
    if (w.end === "salesEnd" && salesEndFallback) return now > salesEndFallback;
    return false;
}
